/**
 * 航拍素材管理弹窗：上传、缩略图预览、勾选纳入渲染、删除
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Modal, Segmented, Space, Spin, Tooltip } from 'antd';
import { message } from '../utils/antdMessage';
import { DeleteOutlined, FolderOpenOutlined, UploadOutlined } from '@ant-design/icons';
import apiFetch from '../utils/apiFetch';
import siteConfig from '../config/site-config.js';
import { isValidWgs84Gps, readFileExifGps } from '../utils/exifGps';
import './OrthoAssetsModal.css';

async function readJson(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.message || `请求失败 ${res.status}`);
  return data;
}

function isOrthoImageFile(file) {
  if (!file?.name) return false;
  if (file.type && file.type.startsWith('image/')) return true;
  return /\.(jpe?g|png|tif|tiff|webp|heic|bmp)$/i.test(file.name);
}

function postObjectToOss(upload, file, objectKey, onProgress) {
  if (!upload?.host || upload.mode === 'disabled') {
    return Promise.reject(new Error(upload?.message || 'OSS 未配置，无法直传'));
  }
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('key', objectKey);
    form.append('policy', upload.policy);
    form.append('OSSAccessKeyId', upload.accessKeyId);
    form.append('Signature', upload.signature || upload.Signature);
    form.append('success_action_status', String(upload.success_action_status || '200'));
    form.append('file', file);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', upload.host);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress({ percent: Math.round((e.loaded / e.total) * 100) });
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`OSS 上传失败 HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('OSS 网络错误'));
    xhr.send(form);
  });
}

/**
 * @param {{
 *   open: boolean,
 *   task: object|null,
 *   onClose: () => void,
 *   onChanged: () => void,
 * }} props
 */
export default function OrthoAssetsModal({ open, task, onClose, onChanged }) {
  const taskId = task?.id;
  const canEdit = task && ['draft', 'ready', 'failed', 'awaiting_approval'].includes(task.status);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadText, setUploadText] = useState('');
  const [assets, setAssets] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [filter, setFilter] = useState('all'); // all | in | out | nogps
  const photoRef = useRef(null);
  const folderRef = useRef(null);

  const token = useMemo(() => localStorage.getItem('token') || '', [open]);
  const apiBase = useMemo(
    () => String(siteConfig.getApiUrl() || '/api').replace(/\/$/, ''),
    []
  );

  const loadAssets = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const data = await readJson(await apiFetch(`/api/ortho/tasks/${taskId}/assets`));
      setAssets(data.assets || []);
      setSelected(new Set());
    } catch (e) {
      message.error(e.message || '加载素材失败');
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    if (open && taskId) loadAssets();
  }, [open, taskId, loadAssets]);

  const previewUrl = (a) =>
    `${apiBase}/ortho/tasks/${taskId}/assets/${a.id}/preview?access_token=${encodeURIComponent(token)}`;

  const visible = useMemo(() => {
    if (filter === 'in') return assets.filter((a) => a.includeInRender);
    if (filter === 'out') return assets.filter((a) => !a.includeInRender);
    if (filter === 'nogps') return assets.filter((a) => !a.hasGps);
    return assets;
  }, [assets, filter]);

  const includedCount = assets.filter((a) => a.includeInRender).length;

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectVisible = () => {
    setSelected(new Set(visible.map((a) => a.id)));
  };

  const clearSelect = () => setSelected(new Set());

  const setInclude = async (ids, include) => {
    if (!ids.length || !canEdit) return;
    try {
      const data = await readJson(await apiFetch(`/api/ortho/tasks/${taskId}/assets/include`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, include })
      }));
      message.success(include ? `已纳入 ${data.updated} 张` : `已排除 ${data.updated} 张`);
      await loadAssets();
      onChanged?.();
    } catch (e) {
      message.error(e.message || '更新失败');
    }
  };

  const deleteIds = async (ids) => {
    if (!ids.length || !canEdit) return;
    Modal.confirm({
      title: `删除选中的 ${ids.length} 张照片？`,
      content: '将从任务与 OSS 中移除，不可恢复。',
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const data = await readJson(await apiFetch(`/api/ortho/tasks/${taskId}/assets/delete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids })
        }));
        message.success(data.message || '已删除');
        await loadAssets();
        onChanged?.();
      }
    });
  };

  const uploadFiles = async (files, { fromFolder = false } = {}) => {
    if (!taskId || !canEdit) return;
    const list = Array.from(files).filter(isOrthoImageFile);
    if (!list.length) {
      message.warning(fromFolder ? '文件夹内没有可识别的图片' : '请选择图片');
      return;
    }
    setUploading(true);
    let ok = 0;
    let fail = 0;
    try {
      for (let i = 0; i < list.length; i += 1) {
        const file = list[i];
        const rel = String(file.webkitRelativePath || file.name).replace(/\\/g, '/');
        const safeName = rel.replace(/[^a-zA-Z0-9._\-\/\u4e00-\u9fa5]/g, '_');
        setUploadText(`上传 ${i + 1}/${list.length}：${rel}`);
        try {
          const credData = await readJson(await apiFetch('/api/ortho/upload/sts', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ taskId, fileName: safeName })
          }));
          const upload = credData.upload || credData;
          if (upload?.mode === 'disabled') throw new Error(upload.message || 'OSS 未配置');
          const objectKey = upload.objectKey || upload.key;
          if (!objectKey) throw new Error('未返回 objectKey');
          await postObjectToOss(upload, file, objectKey, ({ percent }) => {
            setUploadText(`上传 ${i + 1}/${list.length}：${file.name} ${percent}%`);
          });
          let lon;
          let lat;
          try {
            const gps = await readFileExifGps(file);
            if (gps && isValidWgs84Gps(gps.lon, gps.lat)) {
              lon = gps.lon;
              lat = gps.lat;
            }
          } catch (_) { /* ignore */ }
          await readJson(await apiFetch('/api/ortho/upload/register', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              taskId,
              ossKey: objectKey,
              bytes: file.size,
              ...(lon != null ? { lon, lat } : {})
            })
          }));
          ok += 1;
        } catch (oneErr) {
          fail += 1;
          console.warn('[ortho assets] 单张失败', file.name, oneErr);
          if (!fromFolder) throw oneErr;
        }
      }
      setUploadText(fail ? `完成：成功 ${ok}，失败 ${fail}` : `已上传 ${ok} 张`);
      message.success(fail ? `成功 ${ok}，失败 ${fail}` : `已上传 ${ok} 张`);
      await loadAssets();
      onChanged?.();
    } catch (e) {
      setUploadText(e.message || '上传失败');
      message.error(e.message || '上传失败');
    } finally {
      setUploading(false);
      if (photoRef.current) photoRef.current.value = '';
      if (folderRef.current) folderRef.current.value = '';
    }
  };

  const finishAndReady = async () => {
    if (!taskId) return;
    if (assets.length < 1) {
      message.warning('请先上传照片');
      return;
    }
    if (task.status === 'draft') {
      try {
        await readJson(await apiFetch(`/api/ortho/tasks/${taskId}/ready`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        }));
        message.success('素材已就绪');
        onChanged?.();
      } catch (e) {
        message.error(e.message || '就绪失败');
        return;
      }
    }
    onClose?.();
  };

  const selectedArr = [...selected];

  return (
    <Modal
      open={open}
      title={task ? `素材 · ${task.title || `#${task.id}`}` : '管理素材'}
      onCancel={onClose}
      width={920}
      destroyOnClose
      footer={[
        <Button key="close" onClick={onClose}>关闭</Button>,
        <Button key="done" type="primary" disabled={!canEdit || assets.length < 1} onClick={finishAndReady}>
          完成
        </Button>
      ]}
    >
      <input
        ref={photoRef}
        type="file"
        accept="image/*,.jpg,.jpeg,.png,.tif,.tiff"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files?.length) uploadFiles(e.target.files, { fromFolder: false });
        }}
      />
      <input
        ref={(el) => {
          folderRef.current = el;
          if (el) {
            el.setAttribute('webkitdirectory', '');
            el.setAttribute('directory', '');
          }
        }}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files?.length) uploadFiles(e.target.files, { fromFolder: true });
        }}
      />

      <div className="zhxt-ortho-assets-toolbar">
        <div className="zhxt-ortho-assets-toolbar-left">
          <Button
            type="primary"
            icon={<UploadOutlined />}
            disabled={!canEdit || uploading}
            onClick={() => photoRef.current?.click()}
          >
            上传
          </Button>
          <Button
            icon={<FolderOpenOutlined />}
            disabled={!canEdit || uploading}
            onClick={() => folderRef.current?.click()}
          >
            文件夹
          </Button>
          <span className="zhxt-ortho-assets-meta">
            {assets.length} 张 · 纳入 {includedCount}
          </span>
        </div>
        <Segmented
          size="small"
          value={filter}
          onChange={setFilter}
          options={[
            { label: '全部', value: 'all' },
            { label: '已纳入', value: 'in' },
            { label: '已排除', value: 'out' },
            { label: '无 GPS', value: 'nogps' }
          ]}
        />
      </div>

      {uploadText && <div className="zhxt-ortho-assets-upload-tip">{uploadText}</div>}

      {selectedArr.length > 0 && (
        <div className="zhxt-ortho-assets-batch">
          <span className="zhxt-ortho-assets-batch-count">已选 {selectedArr.length}</span>
          <Space wrap size={8}>
            <button type="button" className="zhxt-ortho-assets-text-btn" onClick={selectVisible}>全选</button>
            <button type="button" className="zhxt-ortho-assets-text-btn" onClick={clearSelect}>取消</button>
            <Button size="small" type="primary" ghost disabled={!canEdit} onClick={() => setInclude(selectedArr, true)}>
              纳入
            </Button>
            <Button size="small" disabled={!canEdit} onClick={() => setInclude(selectedArr, false)}>
              排除
            </Button>
            <Button size="small" danger icon={<DeleteOutlined />} disabled={!canEdit} onClick={() => deleteIds(selectedArr)}>
              删除
            </Button>
          </Space>
        </div>
      )}

      <Spin spinning={loading || uploading}>
        <div className="zhxt-ortho-assets-grid">
          {visible.map((a) => (
            <div
              key={a.id}
              className={`zhxt-ortho-asset-card${selected.has(a.id) ? ' is-selected' : ''}${a.includeInRender ? '' : ' is-excluded'}`}
              onClick={() => toggleSelect(a.id)}
              onKeyDown={() => {}}
              role="button"
              tabIndex={0}
            >
              <div className="zhxt-ortho-asset-thumb-wrap">
                <img
                  className="zhxt-ortho-asset-thumb"
                  src={previewUrl(a)}
                  alt={a.name}
                  loading="lazy"
                  onClick={(e) => e.stopPropagation()}
                />
                <Checkbox
                  className="zhxt-ortho-asset-check"
                  checked={selected.has(a.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleSelect(a.id)}
                />
              </div>
              <div className="zhxt-ortho-asset-foot">
                <Tooltip title={a.name}>
                  <div className="zhxt-ortho-asset-name">{a.name}</div>
                </Tooltip>
                <div className="zhxt-ortho-asset-flags">
                  {a.includeInRender ? <span className="ok">纳入</span> : <span className="off">排除</span>}
                  {!a.hasGps && <span className="warn">无GPS</span>}
                </div>
                <div className="zhxt-ortho-asset-ops" onClick={(e) => e.stopPropagation()}>
                  {a.includeInRender ? (
                    <Button type="link" size="small" disabled={!canEdit} onClick={() => setInclude([a.id], false)}>排除</Button>
                  ) : (
                    <Button type="link" size="small" disabled={!canEdit} onClick={() => setInclude([a.id], true)}>纳入</Button>
                  )}
                  <Button type="link" size="small" danger disabled={!canEdit} onClick={() => deleteIds([a.id])}>删</Button>
                </div>
              </div>
            </div>
          ))}
          {!loading && visible.length === 0 && (
            <div className="zhxt-ortho-assets-empty">暂无照片，请先上传</div>
          )}
        </div>
      </Spin>
    </Modal>
  );
}
